// Imports => React
import { useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";

// Imports => Constants
import {
  AcAccordion,
  AcAvatar,
  AcButton,
  AcCard,
  AcContainer,
  AcDivider,
  AcIcon,
  AcTextInput,
  AcTooltip,
  ICONS,
  THEMES,
  TYPES,
} from "@opp/ui-kit";

// Imports => Atoms
import { AcRow, AcColumn, AcHeading } from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import AcCopyText from "../../../atoms/ac-copy-text/ac-copy-text";

const AcProfile = ({
  profile,
  complianceOverviewUrl,
  configurations_kyc,
  t,
  i18n,
}) => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [termsAndServiceUrl, setTermsAndServiceUrl] = useState(
    "https://onlinepaymentplatform.com/en/terms-policies"
  );

  useEffect(() => {
    let locale = i18n.resolvedLanguage.slice(0, 2);
    let localizedTermsAndServiceUrl;

    switch (locale) {
      case "nl":
        localizedTermsAndServiceUrl =
          "https://onlinepaymentplatform.com/nl/voorwaarden-beleid";
        break;
      case "en":
        localizedTermsAndServiceUrl =
          "https://onlinepaymentplatform.com/en/terms-policies";
        break;
      case "de":
        localizedTermsAndServiceUrl =
          "https://onlinepaymentplatform.com/de/bedingungen-richtlinien";
        break;
      case "fr":
        localizedTermsAndServiceUrl =
          "https://onlinepaymentplatform.com/fr/conditions-politiques";
        break;
      default:
        localizedTermsAndServiceUrl =
          "https://onlinepaymentplatform.com/en/terms-policies";
    }

    setTermsAndServiceUrl(localizedTermsAndServiceUrl);
  }, [i18n.resolvedLanguage]);

  const renderGeneralProfileInfo = (profile) => {
    return (
      <>
        {profile.name && (
          <>
            <AcRow className={"h-margin-top-25"}>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-margin-bottom-5"}
              >
                {t("profile.name", "Name", { ns: "account" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={profile.name}
                disabled
              />
            </AcRow>
          </>
        )}

        {profile.contact && profile.contact.phonenumber && (
          <>
            <AcRow>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("profile.phone", "Phone", { ns: "account" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={
                  profile.contact &&
                  profile.contact.phonenumber &&
                  `${profile.contact.phonenumber}`
                }
                disabled
              />
            </AcRow>
          </>
        )}

        {profile.contact && profile.contact.emailaddress && (
          <>
            <AcRow className={"h-margin-top-10"}>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("profile.email", "Email", { ns: "account" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={
                  profile.contact &&
                  profile.contact.emailaddress &&
                  `${profile.contact.emailaddress}`
                }
                disabled
              />
            </AcRow>
          </>
        )}
      </>
    );
  };

  const getBankAccountIcon = (status) => {
    switch (status) {
      case "disapproved":
        return ICONS.CLOSE;
      case "pending":
      case "new":
        return ICONS.CLOCK_HISTORY;
      case "approved":
      default:
        return ICONS.CHECK;
    }
  };

  const getBankAccountTheme = (status) => {
    switch (status) {
      case "disapproved":
        return THEMES.BETA;
      case "new":
      case "pending":
        return THEMES.OMEGA;
      case "approved":
      default:
        return THEMES.CHARLIE;
    }
  };

  const renderBankAccount = (bankAccount) => {
    return (
      <AcContainer className={"h-padding-left-0"}>
        <AcRow
          className={"h-margin-top-10"}
          style={{ alignItems: "center", minHeight: "8rem" }}
        >
          <AcTooltip
            content={t(
              "profile.tooltip-connected-bank-account." + bankAccount.status,
              "All payouts are transferred to this bank account.",
              { ns: "account" }
            )}
            trigger="hover"
            className={`d-none d-lg-inline`}
            placement="right"
          >
            <div>
              <AcColumn
                lg={2}
                className={"h-padding-left-0 h-margin-left-0 h-margin-right-15"}
              >
                <AcAvatar
                  avatar={{
                    type: TYPES.ICON,
                    value: getBankAccountIcon(bankAccount.status),
                    size: 5,
                  }}
                  theme={getBankAccountTheme(bankAccount.status)}
                  className={"h-padding-left-0"}
                />
              </AcColumn>
            </div>
          </AcTooltip>

          <AcColumn sm={12} md={10} lg={8} className={"h-margin-top-10"}>
            <AcRow>
              <AcHeading
                rank={3}
                theme={THEMES.ECHO_LIGHT}
                className={"h-margin-bottom-5"}
              >
                {bankAccount.account.account_name
                  ? bankAccount.account.account_name
                  : t("unknown", "Unknown", { ns: "common" })}
              </AcHeading>
            </AcRow>

            <AcRow>
              <AcHeading
                rank={7}
                theme={THEMES.ECHO_LIGHT}
                className={"h-margin-bottom-5"}
              >
                {bankAccount.account.account_number
                  ? bankAccount.account.account_number
                  : t("unknown", "Unknown", { ns: "common" })}
              </AcHeading>
            </AcRow>
          </AcColumn>

          {bankAccount.status === "disapproved" && configurations_kyc ? (
            <AcButton
              {...getGoToVerificationButtonOptions}
              tag={"a"}
              href={
                bankAccount.verification_url
                  ? bankAccount.verification_url
                  : complianceOverviewUrl
              }
            >
              <AcIcon icon={"bank-plus"} />
              <span>
                {t("compliance-requirement.verify-bank-account", {
                  ns: "account",
                })}
              </span>
            </AcButton>
          ) : (
            ""
          )}
        </AcRow>
      </AcContainer>
    );
  };

  const renderVirtualIban = (virtualIban, key) => {
    return (
      <div className={"h-margin-left-5 h-width-100-p"}>
        {key !== 0 && (
          <AcRow>
            <AcDivider
              className={"h-margin-top-10 h-margin-bottom-20"}
              theme={THEMES.EGGWHITE}
            />
          </AcRow>
        )}

        <AcRow>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("profile.account-holder", "Account holder", { ns: "account" })}
          </AcHeading>
          {virtualIban.account_holder && (
            <AcCopyText t={t} value={virtualIban.account_holder} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.account_holder
                ? virtualIban.account_holder
                : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        <AcRow>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("profile.iban", "IBAN", { ns: "account" })}
          </AcHeading>
          {virtualIban.iban && (
            <AcCopyText t={t} value={virtualIban.iban} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.iban
                ? virtualIban.iban
                : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        <AcRow>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("profile.bic", "BIC", { ns: "account" })}
          </AcHeading>
          {virtualIban.bic && (
            <AcCopyText t={t} value={virtualIban.bic} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.bic ? virtualIban.bic : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        {virtualIban.country && (
          <>
            <AcRow>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("profile.country", "Country", { ns: "account" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={
                  virtualIban.country
                    ? virtualIban.country
                    : t("unknown", { ns: "common" })
                }
                disabled
              />
            </AcRow>
          </>
        )}

        {virtualIban.currency && (
          <>
            <AcRow>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("profile.currency", "Currency", { ns: "account" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={
                  virtualIban.currency
                    ? virtualIban.currency
                    : t("unknown", { ns: "common" })
                }
                disabled
              />
            </AcRow>
          </>
        )}
      </div>
    );
  };

  const handleGoToVerification = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    const url =
      profile.bank_account && profile.bank_account.verification_url
        ? profile.bank_account.verification_url
        : complianceOverviewUrl;

    window.open(url, "_blank").focus();
  };

  const getGoToVerificationButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      title: `${t("compliance-requirement.verify-bank-account", {
        ns: "account",
      })}`,
      className: "h-margin-top-15",
      callback: handleGoToVerification,
    };
  }, [handleGoToVerification, profile, profile.bank_account]);

  return (
    <div className={"ac-profile__column ac-profile__column--half"}>
      <AcRow>
        <AcColumn md={12} lg={6}>
          {profile.bank_account && (
            <AcCard className={"h-padding-bottom-30 h-padding-left-30"}>
              <AcRow className={"h-margin-top-15"}>
                <AcHeading
                  rank={3}
                  theme={THEMES.ECHO_LIGHT}
                  className={"h-padding-y-0 h-margin-y-0"}
                >
                  {t(
                    "profile.connected-bank-account-for-payouts",
                    "Connected bank account",
                    { ns: "account" }
                  )}
                </AcHeading>
              </AcRow>
              {renderBankAccount(profile.bank_account)}
            </AcCard>
          )}
          {profile.virtual_ibans.length > 0 && (
            <div className={"h-padding-bottom-20"}>
              <AcAccordion
                open={accordionOpen}
                title={t(
                  "profile.connected-viban-for-payments",
                  "Virtual IBAN bank account",
                  { ns: "account" }
                )}
                onClick={() => {
                  setAccordionOpen(!accordionOpen);
                }}
                theme={THEMES.WHITE}
                titleClassName={
                  "h-margin-y-5 h-margin-left-10 h-accordion-dark-title"
                }
              >
                {profile.virtual_ibans.map((virtualIban, key) => {
                  return renderVirtualIban(virtualIban, key);
                })}
              </AcAccordion>
            </div>
          )}
          <AcCard className={"h-padding-bottom-30 h-padding-left-30"}>
            {(profile.name ||
              (profile.contact && profile.contact.phonenumber) ||
              (profile.contact && profile.contact.emailaddress)) && (
              <>
                <AcRow className={"h-margin-top-20"}>
                  <AcHeading
                    rank={3}
                    theme={THEMES.ECHO_LIGHT}
                    className={"h-padding-y-0 h-margin-y-0"}
                  >
                    {t("profile.general-info", "General info", {
                      ns: "account",
                    })}
                  </AcHeading>
                </AcRow>
                {renderGeneralProfileInfo(profile)}
              </>
            )}

            <AcRow>
              <AcColumn xs={6}>
                <p className={"h-margin-right-15 h-margin-bottom-0"}>
                  <a
                    href={`${termsAndServiceUrl}`}
                    target="_blank"
                    onClick={() => {
                      window.open(`${termsAndServiceUrl}`, "_blank").focus();
                    }}
                    rel="noreferrer"
                  >
                    {t("profile.terms-and-policies", { ns: "account" })}{" "}
                  </a>
                </p>
              </AcColumn>
              <AcColumn
                xs={6}
                className={"h-flex-h-align-end d-none d-md-flex"}
              >
                <p
                  className={
                    "h-margin-right-15 h-margin-bottom-0 h-text--color-medium"
                  }
                >
                  {t(
                    "profile.incorrect-information",
                    "Incorrect information?",
                    { ns: "account" }
                  )}
                  <AcTooltip
                    content={t(
                      "profile.incorrect-information-tooltip",
                      "Todo",
                      { ns: "account" }
                    )}
                    trigger="hover"
                    className={"d-md-inline h-v-align-middle"}
                  >
                    <span>
                      <i
                        className="ac-icon ac-icon--info-outlined h-margin-left-5 h-text--color-medium h-text--size-medium"
                        role="img"
                        alt="icon: info-outlined"
                        title=""
                      ></i>
                    </span>
                  </AcTooltip>
                </p>
              </AcColumn>
            </AcRow>
          </AcCard>
        </AcColumn>
      </AcRow>
    </div>
  );
};

export default withTranslation(["account", "common"])(
  withStore(observer(AcProfile))
);
